import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wand, Unlink, ChevronDown, ChevronRight, ChevronsDown, HelpCircle, Folder, FileText, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formatDateTime } from '../lib/text';
import { SectionDocument, SectionChild } from '../lib/types';
import { Pill, priorityStyles, priorityRank, statusStyles as sharedStatusStyles } from './DocTypeBlock';

// Adds 'draft' (linked but outdated) to DocTypeBlock's DocStatus.
type DocStatus = 'published' | 'draft' | 'candidates' | 'missing' | 'pending';

const statusStyles: Record<DocStatus, { label: string; cls: string }> = {
  ...sharedStatusStyles,
  draft: { label: 'Draft', cls: 'bg-amber-50 text-amber-700 border border-amber-200' },
};

const statusRank: Record<DocStatus, number> = {
  missing: 0,
  candidates: 1,
  draft: 2,
  published: 3,
  pending: 4,
};

export function SectionDocumentsTab({ documents, subsections = [], onReload }: {
  documents: SectionDocument[];
  subsections?: SectionChild[];
  onReload: () => Promise<void> | void;
}) {
  const { authFetch } = useAuth();
  const { refresh: refreshWorkspace } = useWorkspace();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const handleUnlink = async (typeId: string, fileId: string) => {
    if (!window.confirm('Unlink this file from the document type?')) return;
    await authFetch(`/workspace/links/${typeId}/${fileId}`, { method: 'DELETE' });
    await Promise.all([onReload(), refreshWorkspace()]);
  };

  const handleRelink = async (typeId: string, fileId: string) => {
    await authFetch(`/workspace/links/${typeId}/${fileId}`, { method: 'POST' });
    await Promise.all([onReload(), refreshWorkspace()]);
  };

  if (documents.length === 0 && subsections.length === 0) {
    return (
      <div className="text-center py-12 text-coolslate">
        No contents in this section.
      </div>
    );
  }

  const rows = documents.map(def => {
    const linked = def.linked_files;
    const isPending = def.pending;
    // A pending row keeps its real status badge, so progress stays visible.
    let status: DocStatus;
    if (linked.length > 0) {
      const anyOutdated = linked.some(ld => ld.latest_version > ld.version);
      status = anyOutdated ? 'draft' : 'published';
    } else if (def.candidates_count > 0) {
      status = 'candidates';
    } else if (isPending) {
      status = 'pending';
    } else {
      status = 'missing';
    }
    const lastUpdatedRaw = linked.reduce<string | null>((acc, ld) => {
      if (!ld.linked_at) return acc;
      if (!acc || ld.linked_at > acc) return ld.linked_at;
      return acc;
    }, null);
    return { def, linked, status, lastUpdatedRaw, isPending };
  });

  const sortSubs = (a: SectionChild, b: SectionChild) =>
    a.name.localeCompare(b.name);
  const sortRows = (a: typeof rows[number], b: typeof rows[number]) => {
    const pr = priorityRank[a.def.priority] - priorityRank[b.def.priority];
    if (pr !== 0) return pr;
    const sr = statusRank[a.status] - statusRank[b.status];
    if (sr !== 0) return sr;
    return a.def.name.localeCompare(b.def.name);
  };
  const activeSubs = subsections.filter(s => s.enabled).sort(sortSubs);
  const pendingSubs = subsections.filter(s => !s.enabled).sort(sortSubs);
  const activeRows = rows.filter(r => !r.isPending).sort(sortRows);
  const pendingRows = rows.filter(r => r.isPending).sort(sortRows);
  const pendingTotal = pendingSubs.length + pendingRows.length;
  const [pendingOpen, setPendingOpen] = useState(false);

  const toggleExpand = (id: string) =>
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const renderSubRow = (sub: SectionChild, subPending: boolean) => (
    <tr key={`sub-${sub.id}`} className="border-t border-softgrey">
      <td className="py-3 pl-3 pr-1 align-middle">
        <Folder className={`w-4 h-4 ${subPending ? 'text-coolslate/60' : 'text-brand'}`} />
      </td>
      <td className="py-3 px-3 align-middle">
        <Link
          to={`/workspace/${sub.id}`}
          className={`text-sm font-medium transition-colors ${subPending ? 'text-coolslate/60 hover:text-brand-dark' : 'text-graphite hover:text-brand-dark'}`}
        >
          {sub.name}
        </Link>
        {sub.intro && (
          <p className={'text-[12px] mt-0.5 line-clamp-1 text-coolslate' + (subPending ? '/60' : '')}>{sub.intro}</p>
        )}
      </td>
      <td className="py-3 px-3 align-middle" colSpan={3} />
    </tr>
  );

  const renderDocRow = ({ def, linked, status, lastUpdatedRaw, isPending }: typeof rows[number]) => {
    const isExpanded = !!expanded[def.id];
    const canExpand = linked.length > 0;
    const style = statusStyles[status];
    const priorityStyle = priorityStyles[def.priority];
    const pendingQuestions = def.pending_questions ?? [];
    return (
      <Fragment key={def.id}>
        <tr
          className={`border-t border-softgrey ${canExpand ? 'cursor-pointer hover:bg-offwhite/40' : ''}`}
          onClick={() => canExpand && toggleExpand(def.id)}
        >
          <td className="py-3 pl-3 pr-1 align-middle">
            <div className="flex items-center gap-1.5">
              <FileText className={`w-4 h-4 ${isPending ? 'text-coolslate/60' : 'text-coolslate'}`} />
              {canExpand && (
                isExpanded
                  ? <ChevronDown className="w-3.5 h-3.5 text-coolslate" />
                  : <ChevronRight className="w-3.5 h-3.5 text-coolslate" />
              )}
            </div>
          </td>
          <td className="py-3 px-3 align-middle">
            <Link
              to={`/types/${def.id}`}
              onClick={e => e.stopPropagation()}
              className={`text-sm font-medium transition-colors ${isPending ? 'text-coolslate/60 hover:text-brand-dark' : 'text-graphite hover:text-brand-dark'}`}
            >
              {def.name}
            </Link>
            {def.description && (
              <p className={'text-[12px] mt-0.5 line-clamp-1 text-coolslate' + (isPending ? '/60' : '')}>{def.description}</p>
            )}
            {def.notes && (
              <div className={`mt-1.5 flex items-start gap-1.5 px-2 py-1 text-[12px] text-sky-900 leading-relaxed ${isPending ? 'opacity-60' : ''}`}>
                <Info className="w-3 h-3 text-sky-600 shrink-0 mt-0.5" />
                <span className="whitespace-pre-wrap min-w-0">{def.notes}</span>
              </div>
            )}
            {isPending && pendingQuestions.length > 0 && (
              <div className="mt-2 space-y-1">
                {pendingQuestions.map(q => (
                  <Link
                    key={q.field_id}
                    to={`/workspace/${q.section_id ?? ''}?field=${encodeURIComponent(q.field_id)}`}
                    onClick={e => e.stopPropagation()}
                    className="flex items-start gap-1.5 text-[12px] text-brand hover:text-brand-dark transition-colors"
                  >
                    <HelpCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span className="underline-offset-2 hover:underline">{q.label}</span>
                  </Link>
                ))}
              </div>
            )}
          </td>
          <td className="py-3 px-3 align-middle">
            {priorityStyle && <Pill style={priorityStyle} />}
          </td>
          <td className="py-3 px-3 align-middle">
            <Pill style={style} />
          </td>
          <td className="py-3 px-3 align-middle text-[13px] text-coolslate">
            {lastUpdatedRaw ? formatDateTime(lastUpdatedRaw) : '—'}
          </td>
        </tr>
        {isExpanded && linked.length > 0 && (
          <tr className="bg-offwhite/30 border-t border-softgrey">
            <td />
            <td colSpan={4} className="py-3 px-3">
              <div className="space-y-1.5">
                {linked.map(ld => (
                  <div
                    key={ld.file_id}
                    className="flex items-center gap-3 px-3 py-2 border border-sage/30 bg-white rounded-lg"
                  >
                    <Link to={`/files/${ld.file_id}`} className="flex-1 min-w-0 hover:opacity-70 transition-opacity">
                      <span className="text-sm font-medium text-graphite truncate block">
                        {ld.source_filename}
                        {ld.version > 1 && (
                          <span className="ml-1.5 text-[11px] font-mono text-coolslate bg-offwhite border border-softgrey rounded px-1 py-px">
                            v{ld.version}
                          </span>
                        )}
                      </span>
                      {ld.source_path && (
                        <span className="text-[12px] text-coolslate truncate block mt-0.5">{ld.source_path}</span>
                      )}
                    </Link>
                    {ld.latest_version > ld.version && (
                      <button
                        onClick={() => handleRelink(def.id, ld.file_id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-md transition-colors cursor-pointer shrink-0"
                      >
                        <Wand className="w-3 h-3" />
                        New version
                      </button>
                    )}
                    <button
                      onClick={() => handleUnlink(def.id, ld.file_id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-md transition-colors cursor-pointer shrink-0"
                    >
                      <Unlink className="w-3 h-3" />
                      Unlink
                    </button>
                  </div>
                ))}
              </div>
            </td>
          </tr>
        )}
      </Fragment>
    );
  };

  return (
    <div>
      <div className="mb-4 border border-softgrey rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-offwhite/60 text-[11px] uppercase tracking-wide text-coolslate">
            <tr>
              <th className="w-8" />
              <th className="text-left font-semibold py-2.5 px-3 w-full">Name</th>
              <th className="text-left font-semibold py-2.5 px-3 w-32">Priority</th>
              <th className="text-left font-semibold py-2.5 px-3 w-80">Status</th>
              <th className="text-left font-semibold py-2.5 px-3 w-80">Last&nbsp;updated</th>
            </tr>
          </thead>
          <tbody>
            {activeSubs.map(sub => renderSubRow(sub, false))}
            {activeRows.map(renderDocRow)}
            {pendingOpen && pendingSubs.map(sub => renderSubRow(sub, true))}
            {pendingOpen && pendingRows.map(renderDocRow)}
          </tbody>
        </table>
      </div>

      {pendingTotal > 0 && !pendingOpen && (
        <button
          type="button"
          onClick={() => setPendingOpen(true)}
          className="mb-8 w-full flex items-center justify-center gap-2 py-2 text-sm text-coolslate/70 hover:text-coolslate transition-colors cursor-pointer"
          aria-expanded={false}
        >
          <ChevronsDown className="w-3.5 h-3.5 shrink-0" />
          <span>Other documents may be relevant depending on your business</span>
          <ChevronsDown className="w-3.5 h-3.5 shrink-0" />
        </button>
      )}
    </div>
  );
}
